import React, { useState, useEffect } from 'react';
import {
    Search, Calendar, PlusCircle, Filter, DollarSign,
    FileText, AlertCircle, CheckCircle, Clock, Repeat, Edit, Trash2, PauseCircle, PlayCircle, Mail, Printer, Send, FileSpreadsheet, Download,
    Link, Unlink, Zap, Check, X, Loader2, MessageSquare
} from 'lucide-react';
import api from '../services/api';
import Modal from '../components/Modal';

const FinanceiroScreen = ({ companyData }) => {
    const [activeTab, setActiveTab] = useState('management'); // 'management' | 'issue' | 'recurrence'
    const [invoices, setInvoices] = useState([]);
    const [recurrences, setRecurrences] = useState([]);
    const [externalCompanies, setExternalCompanies] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filtros
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
        return { start, end };
    });

    // Novo Boleto / Recorrência
    const initialInvoiceState = {
        payerId: '',
        payerName: '',
        amount: '',
        dueDate: '', // Para 1x ou 1ª parcela
        description: '',
        type: 'one_time', // one_time, installments, recurring
        installments: 1, // Quantidade de parcelas
        recurrenceInterval: 'monthly', // weekly, monthly, custom
        customIntervalDays: 30,
        recurrenceDuration: 'indefinite', // fixed, indefinite
        recurrenceTotalTimes: 12,
        integrationId: '',
        sendEmail: false,
        emitNfse: false, // 🆕 Emitir NFS-e vinculada
        nfseConfig: {
            tributacaoNacional: '',
            itemLc116: '',
            aliquota: 5.0,
            description: ''
        }
    };
    const [newItem, setNewItem] = useState(initialInvoiceState);

    // 🆕 Configuração Padrão Fiscal e Vínculo NFS-e
    const [nfseDefaultConfig, setNfseDefaultConfig] = useState(null);
    const [quickAmountModalRecurrence, setQuickAmountModalRecurrence] = useState(null);
    const [quickAmountValue, setQuickAmountValue] = useState('');
    const [linkNfseModalRecurrence, setLinkNfseModalRecurrence] = useState(null);
    const [linkNfseForm, setLinkNfseForm] = useState({
        tributacaoNacional: '',
        itemLc116: '',
        aliquota: 5.0,
        description: ''
    });
    const [comboSendingId, setComboSendingId] = useState(null);

    // Estado de Edição de Recorrência
    const [editingRecurrence, setEditingRecurrence] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Recurrence Search & Pagination
    const [recurrenceSearch, setRecurrenceSearch] = useState('');
    const [recurrencePage, setRecurrencePage] = useState(1);
    const [recurrenceTotalPages, setRecurrenceTotalPages] = useState(1);
    const [recurrenceTotalItems, setRecurrenceTotalItems] = useState(0);

    useEffect(() => {
        loadFiscalDefaults();
        loadExternalCompanies();
        if (activeTab === 'management') loadInvoices();
        if (activeTab === 'recurrence') loadRecurrences();
    }, [activeTab, statusFilter, dateRange, recurrencePage, recurrenceSearch]);

    const loadFiscalDefaults = async () => {
        try {
            const res = await api.get('/integrations');
            if (res.data && res.data.nfse) {
                const n = res.data.nfse;
                setNfseDefaultConfig(n);
                setNewItem(prev => ({
                    ...prev,
                    nfseConfig: {
                        tributacaoNacional: n.tributacaoNacional || n.itemLc116 || '01.07.01',
                        itemLc116: n.itemLc116 || '01.07',
                        aliquota: n.aliquota !== undefined ? n.aliquota : 5.0,
                        description: prev.description || ''
                    }
                }));
            }
        } catch (_) {}
    };


    const loadExternalCompanies = async () => {
        try {
            const res = await api.get('/external-companies?limit=1000'); // Load all for dropdown
            if (res.data && Array.isArray(res.data.data)) {
                setExternalCompanies(res.data.data);
            } else if (Array.isArray(res.data)) {
                setExternalCompanies(res.data);
            } else {
                setExternalCompanies([]);
            }
        } catch (error) { console.error("Erro ao carregar empresas externas", error); }
    };

    const loadInvoices = async () => {
        setLoading(true);
        try {
            const params = { status: statusFilter };
            if (dateRange.start && dateRange.end) {
                params.start = dateRange.start;
                params.end = dateRange.end;
            }
            const res = await api.get('/invoices', { params });
            setInvoices(res.data);
        } catch (error) { console.error("Erro ao carregar faturas", error); }
        finally { setLoading(false); }
    };

    const loadRecurrences = async () => {
        setLoading(true);
        try {
            const params = {
                page: recurrencePage,
                limit: 30,
                search: recurrenceSearch
            };
            const res = await api.get('/recurrences', { params });
            if (res.data.data) {
                setRecurrences(res.data.data);
                setRecurrenceTotalPages(res.data.pages);
                setRecurrenceTotalItems(res.data.total);
            } else {
                setRecurrences(res.data); // Fallback for old API if needed
            }
        } catch (error) { console.error("Erro ao carregar recorrências", error); }
        finally { setLoading(false); }
    };

    const handleClientSelect = (e) => {
        const selectedId = e.target.value;
        const company = externalCompanies.find(c => c._id === selectedId);
        if (company) {
            setNewItem({
                ...newItem,
                payerId: company._id,
                payerName: company.name,
                integrationId: company.defaultIntegration || '' // 🆕 Auto-select integration
            });
        } else {
            setNewItem({ ...newItem, payerId: '', payerName: '', integrationId: '' });
        }
    };

    const handleCreate = async () => {
        if (!newItem.payerId || !newItem.amount) return alert("Preencha o cliente e o valor.");

        try {
            if (newItem.type === 'one_time') {
                if (!newItem.dueDate) return alert("Informe o vencimento.");
                await api.post('/invoices', {
                    payerId: newItem.payerId,
                    payerName: newItem.payerName,
                    amount: newItem.amount,
                    dueDate: newItem.dueDate,
                    description: newItem.description,
                    integrationId: newItem.integrationId,
                    sendEmail: newItem.sendEmail,
                    emitNfse: newItem.emitNfse,
                    nfseConfig: newItem.nfseConfig
                });
                alert(newItem.emitNfse ? "Boleto e NFS-e vinculada gerados com sucesso!" : "Boleto criado com sucesso!");
            } else if (newItem.type === 'installments') {
                if (!newItem.dueDate) return alert("Informe o vencimento da 1ª parcela.");
                // Criar N faturas
                const firstDate = new Date(newItem.dueDate);
                const baseAmount = parseFloat(newItem.amount) / parseInt(newItem.installments);

                for (let i = 0; i < newItem.installments; i++) {
                    const date = new Date(firstDate);
                    date.setMonth(date.getMonth() + i);
                    await api.post('/invoices', {
                        payerId: newItem.payerId,
                        payerName: newItem.payerName,
                        amount: baseAmount.toFixed(2),
                        dueDate: date,
                        description: `${newItem.description} (${i + 1}/${newItem.installments})`,
                        installmentNumber: i + 1,
                        totalInstallments: newItem.installments,
                        integrationId: newItem.integrationId,
                        sendEmail: newItem.sendEmail
                    });
                }
                alert(`Carnê gerado com ${newItem.installments} parcelas!`);
            } else if (newItem.type === 'recurring') {
                await api.post('/recurrences', {
                    payerId: newItem.payerId,
                    payerName: newItem.payerName,
                    amount: newItem.amount,
                    description: newItem.description,
                    interval: newItem.recurrenceInterval,
                    customIntervalDays: newItem.customIntervalDays,
                    durationMode: newItem.recurrenceDuration,
                    totalInstallments: newItem.recurrenceDuration === 'fixed' ? newItem.recurrenceTotalTimes : null,
                    nextRun: newItem.dueDate || new Date(),
                    integrationId: newItem.integrationId,
                    emitNfse: newItem.emitNfse,
                    nfseConfig: newItem.nfseConfig
                });
                alert(newItem.emitNfse ? "Recorrência com emissão automática de NFS-e configurada com sucesso!" : "Recorrência configurada com sucesso!");
            }

            setNewItem(initialInvoiceState);
            setActiveTab('management'); // Ou recurrence
        } catch (error) {
            alert("Erro ao criar cobrança.");
            console.error(error);
        }
    };

    const toggleRecurrenceStatus = async (rec) => {
        try {
            const newStatus = rec.status === 'active' ? 'paused' : 'active';
            await api.put(`/recurrences/${rec._id}`, { status: newStatus });
            loadRecurrences();
        } catch (error) { alert("Erro ao alterar status"); }
    };

    const deleteRecurrence = async (id) => {
        if (window.confirm("Excluir esta recorrência?")) {
            try {
                await api.delete(`/recurrences/${id}`);
                loadRecurrences();
            } catch (error) { alert("Erro ao excluir"); }
        }
    };

    const handleEditRecurrence = (rec) => {
        setEditingRecurrence({
            ...rec,
            recurrenceInterval: rec.interval,
            customIntervalDays: rec.customIntervalDays || 30,
            recurrenceDuration: rec.durationMode || 'indefinite',
            recurrenceTotalTimes: rec.totalInstallments || 12,
            dueDate: rec.nextRun ? new Date(rec.nextRun).toISOString().split('T')[0] : ''
        });
        setModalOpen(true);
    };

    const handleSaveRecurrence = async () => {
        if (!editingRecurrence.amount || !editingRecurrence.dueDate) return alert("Preencha os campos obrigatórios.");

        try {
            await api.put(`/recurrences/${editingRecurrence._id}`, {
                amount: editingRecurrence.amount,
                description: editingRecurrence.description,
                interval: editingRecurrence.recurrenceInterval,
                customIntervalDays: editingRecurrence.customIntervalDays,
                durationMode: editingRecurrence.recurrenceDuration,
                totalInstallments: editingRecurrence.recurrenceDuration === 'fixed' ? editingRecurrence.recurrenceTotalTimes : null,
                nextRun: editingRecurrence.dueDate,
                status: editingRecurrence.status
            });
            alert("Recorrência atualizada!");
            setModalOpen(false);
            setEditingRecurrence(null);
            loadRecurrences();
        } catch (error) {
            alert("Erro ao atualizar recorrência.");
            console.error(error);
        }
    };

    // 🆕 Handlers de Edição Rápida de Valor e Vínculo com NFS-e
    const handleOpenQuickAmountModal = (rec) => {
        setQuickAmountModalRecurrence(rec);
        setQuickAmountValue(rec.amount || '');
    };

    const handleSaveQuickAmount = async () => {
        if (!quickAmountValue || parseFloat(quickAmountValue) <= 0) {
            return alert("Informe um valor válido e positivo.");
        }
        try {
            const res = await api.patch(`/recurrences/${quickAmountModalRecurrence._id}/amount`, {
                amount: parseFloat(quickAmountValue)
            });
            alert(res.data?.message || "Valor atualizado e sincronizado com a NFS-e com sucesso!");
            setQuickAmountModalRecurrence(null);
            loadRecurrences();
        } catch (e) {
            alert("Erro ao atualizar valor: " + (e.response?.data?.error || e.message));
        }
    };

    const handleOpenLinkNfseModal = (rec) => {
        setLinkNfseModalRecurrence(rec);
        const defN = nfseDefaultConfig || {};
        setLinkNfseForm({
            tributacaoNacional: rec.nfseConfig?.tributacaoNacional || defN.tributacaoNacional || defN.itemLc116 || '01.07.01',
            itemLc116: rec.nfseConfig?.itemLc116 || defN.itemLc116 || '01.07',
            aliquota: rec.nfseConfig?.aliquota !== undefined ? rec.nfseConfig.aliquota : (defN.aliquota || 5.0),
            description: rec.nfseConfig?.description || rec.description || 'Prestação de serviços'
        });
    };

    const handleSaveLinkNfse = async () => {
        try {
            const res = await api.post(`/recurrences/${linkNfseModalRecurrence._id}/link-nfse`, {
                nfseConfig: linkNfseForm
            });
            alert(res.data?.message || "NFS-e vinculada com sucesso!");
            setLinkNfseModalRecurrence(null);
            loadRecurrences();
        } catch (e) {
            alert("Erro ao vincular NFS-e: " + (e.response?.data?.error || e.message));
        }
    };

    const handleUnlinkNfse = async (rec) => {
        if (!window.confirm("Tem certeza que deseja desvincular a Nota Fiscal desta recorrência?")) return;
        try {
            const res = await api.post(`/recurrences/${rec._id}/link-nfse`, { unlink: true });
            alert(res.data?.message || "NFS-e desvinculada com sucesso!");
            setLinkNfseModalRecurrence(null);
            loadRecurrences();
        } catch (e) {
            alert("Erro ao desvincular NFS-e: " + (e.response?.data?.error || e.message));
        }
    };

    const handleTriggerCombo = async (rec) => {
        if (!window.confirm(`Deseja disparar AGORA a cobrança e emissão da NFS-e para ${rec.payerName || rec.payerId?.name} no valor de R$ ${rec.amount?.toFixed(2)}?`)) {
            return;
        }
        try {
            const res = await api.post(`/recurrences/${rec._id}/trigger-combo`);
            alert(res.data?.message || "Cobrança e NFS-e geradas com sucesso!");
            loadRecurrences();
            if (activeTab === 'management') loadInvoices();
        } catch (e) {
            alert("Erro ao disparar cobrança e nota: " + (e.response?.data?.error || e.message));
        }
    };

    const handleSendCombo = async (invoice) => {
        if (!window.confirm(`Enviar por e-mail o Boleto em PDF + DANFSe em PDF + XML da nota para ${invoice.payerName}?`)) {
            return;
        }
        setComboSendingId(invoice._id);
        try {
            const res = await api.post(`/invoices/${invoice._id}/send-combo`);
            alert(res.data?.message || "Combo enviado com sucesso por e-mail!");
        } catch (e) {
            alert("Erro ao enviar combo: " + (e.response?.data?.error || e.message));
        } finally {
            setComboSendingId(null);
        }
    };

    const handleSendComboWhatsApp = async (invoice) => {
        const phone = prompt("Confirme o WhatsApp para envio do Boleto e da Nota Fiscal:", invoice.payerId?.phone || '');
        if (!phone) return;
        setComboSendingId(invoice._id);
        try {
            const res = await api.post(`/invoices/${invoice._id}/send-combo-whatsapp`, { phone });
            alert(res.data?.message || "Dados do boleto e da NFS-e enviados com sucesso pelo WhatsApp!");
        } catch (e) {
            alert("Erro ao enviar pelo WhatsApp: " + (e.response?.data?.error || e.message));
        } finally {
            setComboSendingId(null);
        }
    };

    const markAsPaid = async (id) => {
        if (window.confirm("Marcar este boleto como PAGO?")) {
            try {
                await api.put(`/invoices/${id}`, { status: 'paid' });
                loadInvoices();
            } catch (error) { alert("Erro ao atualizar status"); }
        }
    };

    const deleteInvoice = async (id) => {
        if (window.confirm("Tem certeza que deseja cancelar/excluir este boleto?")) {
            try {
                await api.delete(`/invoices/${id}`);
                loadInvoices();
            } catch (error) { alert("Erro ao excluir"); }
        }
    };

    const sendInvoiceEmail = async (id) => {
        if (!window.confirm("Enviar boleto por email para o cliente?")) return;
        try {
            await api.post(`/invoices/${id}/send-email`);
            alert("Email enviado com sucesso!");
        } catch (error) {
            alert(error.response?.data?.error || "Erro ao enviar email.");
        }
    };

    const printInvoice = (invoice) => {
        if (invoice.pdfUrl) {
            window.open(invoice.pdfUrl, '_blank');
            return;
        }

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Recibo - ${invoice.payerName || 'Cliente'}</title>
                    <style>
                        body { font-family: sans-serif; padding: 40px; }
                        .header { text-align: center; border-bottom: 2px solid #ccc; padding-bottom: 20px; margin-bottom: 30px; }
                        h1 { margin: 0; color: #333; }
                        .info { margin-bottom: 20px; }
                        .row { display: flex; justify-content: space-between; border-bottom: 1px solid #eee; padding: 10px 0; }
                        .label { font-weight: bold; color: #555; }
                        .total { font-size: 1.5em; font-weight: bold; margin-top: 30px; text-align: right; color: #2563eb; }
                        .status { font-weight: bold; text-transform: uppercase; color: ${invoice.status === 'paid' ? 'green' : (invoice.status === 'canceled' ? 'red' : 'orange')}; }
                        @media print { .no-print { display: none; } } 
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>${companyData?.name || 'Recibo de Pagamento'}</h1>
                        <p>${companyData?.activeUser?.email || ''}</p>
                    </div>
                    <div class="content">
                        <div class="row"><span class="label">Cliente:</span> <span>${invoice.payerName}</span></div>
                        <div class="row"><span class="label">Referência:</span> <span>${invoice.description || 'Serviços Prestados'}</span></div>
                        <div class="row"><span class="label">Data de Vencimento:</span> <span>${new Date(invoice.dueDate).toLocaleDateString()}</span></div>
                        <div class="row"><span class="label">Status:</span> <span class="status">${invoice.status === 'paid' ? 'PAGO' : (invoice.status === 'canceled' ? 'CANCELADO' : 'PENDENTE')}</span></div>
                        
                        <div class="total">
                            Valor Total: R$ ${parseFloat(invoice.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                    </div>
                    <div style="margin-top: 50px; text-align: center; font-size: 0.8em; color: #777;">
                        Gerado em ${new Date().toLocaleString()}
                    </div>
                    <script>
                        window.onload = function() { window.print(); }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const getStatusBadge = (status, dueDate) => {
        const isOverdue = new Date(dueDate) < new Date() && status === 'pending';
        if (status === 'paid') return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Recebido</span>;
        if (status === 'canceled') return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold flex items-center"><FileText className="w-3 h-3 mr-1" /> Cancelado</span>;
        if (isOverdue) return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> Atrasado</span>;
        return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold flex items-center"><Clock className="w-3 h-3 mr-1" /> A receber</span>;
    };

    // 🆕 Derived state for filtered invoices to use in Export/Print
    const filteredInvoices = invoices.filter(i => i.payerName.toLowerCase().includes(searchTerm.toLowerCase()));

    const handlePrintList = () => {
        const printWindow = window.open('', '_blank', 'width=900,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Relatório Financeiro</title>
                    <style>
                        body { font-family: sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
                        th { background-color: #f2f2f2; }
                        .header { margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px; }
                        h1 { margin: 0; font-size: 18px; }
                        .summary { margin-top: 20px; font-weight: bold; text-align: right; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Relatório Financeiro - ${companyData?.name || 'Empresa'}</h1>
                        <p>Gerado em: ${new Date().toLocaleString()}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Descrição</th>
                                <th>Vencimento</th>
                                <th>Status</th>
                                <th style="text-align: right;">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredInvoices.map(inv => `
                                <tr>
                                    <td>${inv.payerName}</td>
                                    <td>${inv.description || '-'}</td>
                                    <td>${new Date(inv.dueDate).toLocaleDateString()}</td>
                                    <td>${inv.status === 'paid' ? 'PAGO' : (inv.status === 'canceled' ? 'CANCELADO' : 'PENDENTE')}</td>
                                    <td style="text-align: right;">R$ ${inv.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="summary">
                        Total: R$ ${filteredInvoices.reduce((acc, curr) => acc + curr.amount, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                    <script>window.onload = function() { window.print(); }</script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleExportExcel = () => {
        const headers = ["Cliente", "Descrição", "Vencimento", "Status", "Valor"];
        const rows = filteredInvoices.map(inv => [
            inv.payerName,
            inv.description || '',
            new Date(inv.dueDate).toLocaleDateString(),
            inv.status === 'paid' ? 'PAGO' : (inv.status === 'canceled' ? 'CANCELADO' : 'PENDENTE'),
            inv.amount.toFixed(2).replace('.', ',')
        ]);

        const csvContent = [
            headers.join(";"),
            ...rows.map(e => e.join(";"))
        ].join("\n");

        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `financeiro_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrintRecurrences = () => {
        const printWindow = window.open('', '_blank', 'width=900,height=600');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Relatório de Recorrências</title>
                    <style>
                        body { font-family: sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
                        th { background-color: #f2f2f2; }
                        .header { margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px; }
                        h1 { margin: 0; font-size: 18px; }
                        .summary { margin-top: 20px; font-weight: bold; text-align: right; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Relatório de Recorrências - ${companyData?.name || 'Empresa'}</h1>
                        <p>Gerado em: ${new Date().toLocaleString()}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Frequência</th>
                                <th>Status</th>
                                <th style="text-align: right;">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${recurrences.map(rec => `
                                <tr>
                                    <td>${rec.payerName || rec.payerId?.name || 'Cliente'}</td>
                                    <td>${rec.interval === 'monthly' ? 'Mensal' : rec.interval === 'weekly' ? 'Semanal' : `${rec.customIntervalDays} dias`}</td>
                                    <td>${rec.status === 'active' ? 'Ativo' : 'Pausado'}</td>
                                    <td style="text-align: right;">R$ ${rec.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="summary">
                        Total Registros: ${recurrences.length}
                    </div>
                    <script>window.onload = function() { window.print(); }</script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleExportRecurrences = () => {
        const headers = ["Cliente", "Frequência", "Status", "Valor"];
        const rows = recurrences.map(rec => [
            rec.payerName || rec.payerId?.name || 'Cliente',
            rec.interval === 'monthly' ? 'Mensal' : rec.interval === 'weekly' ? 'Semanal' : `${rec.customIntervalDays} dias`,
            rec.status === 'active' ? 'Ativo' : 'Pausado',
            rec.amount.toFixed(2).replace('.', ',')
        ]);

        const csvContent = [
            headers.join(";"),
            ...rows.map(e => e.join(";"))
        ].join("\n");

        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `recorrencias_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="flex-1 flex flex-col h-full bg-gray-50 overflow-hidden">
            <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Gestão Financeira</h1>
                    <p className="text-sm text-gray-500">Boletos, Parcelamentos e Recorrências</p>
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg">
                    <button onClick={() => setActiveTab('management')} className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'management' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>Boletos</button>
                    <button onClick={() => setActiveTab('recurrence')} className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'recurrence' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>Recorrências</button>
                    <button onClick={() => setActiveTab('issue')} className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'issue' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>Emitir</button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                {activeTab === 'management' && (
                    <div className="max-w-6xl mx-auto space-y-6">
                        {/* Filtros e Lista de Boletos (Similar ao anterior) */}
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                                <input type="text" placeholder="Nome do cliente..." className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                            <div className="flex items-center gap-2 bg-white border px-3 rounded-lg shadow-sm">
                                <Calendar className="text-gray-400 w-5 h-5" />
                                <input type="date" className="outline-none text-sm text-gray-600 bg-transparent" value={dateRange.start} onChange={e => setDateRange({ ...dateRange, start: e.target.value })} />
                                <span className="text-gray-400">-</span>
                                <input type="date" className="outline-none text-sm text-gray-600 bg-transparent" value={dateRange.end} onChange={e => setDateRange({ ...dateRange, end: e.target.value })} />
                            </div>
                        </div>

                        <div className="flex justify-between items-center">
                            {/* Status Filters */}
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { id: 'all', label: 'Todos' },
                                    { id: 'overdue', label: 'Atrasados' },
                                    { id: 'pending', label: 'A receber' },
                                    { id: 'paid', label: 'Recebidos' }
                                ].map(filter => (
                                    <button
                                        key={filter.id}
                                        onClick={() => setStatusFilter(filter.id)}
                                        className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all border ${statusFilter === filter.id
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105'
                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                            }`}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>

                            {/* Print/Export Buttons */}
                            <div className="flex gap-2">
                                <button onClick={handlePrintList} className="bg-white border hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm" title="Imprimir Lista">
                                    <Printer className="w-4 h-4 mr-2" /> Imprimir
                                </button>
                                <button onClick={handleExportExcel} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm" title="Exportar CSV">
                                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-gray-50 text-gray-600 uppercase font-bold border-b">
                                    <tr>
                                        <th className="px-6 py-3">Cliente</th>
                                        <th className="px-6 py-3 text-center">Status</th>
                                        <th className="px-6 py-3 text-center">Vencimento</th>
                                        <th className="px-6 py-3 text-right">Valor</th>
                                        <th className="px-6 py-3 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredInvoices.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-gray-500">Nenhum boleto encontrado.</td></tr> : filteredInvoices.map(invoice => (
                                        <tr key={invoice._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-gray-800">{invoice.payerName}</div>
                                                <div className="text-xs text-gray-500">{invoice.description}</div>
                                                {(invoice.nfseId || invoice.nfseNumber) && (
                                                    <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded font-medium mt-1">
                                                        📄 NFS-e nº {invoice.nfseNumber || 'Emitida'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 flex justify-center">{getStatusBadge(invoice.status, invoice.dueDate)}</td>
                                            <td className="px-6 py-4 text-center text-gray-600">{new Date(invoice.dueDate).toLocaleDateString()}</td>
                                            <td className="px-6 py-4 text-right font-bold text-blue-600">R$ {invoice.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                            <td className="px-6 py-4 text-center flex justify-center gap-2">

                                                <button onClick={() => printInvoice(invoice)} className="text-gray-500 hover:bg-gray-100 p-1 rounded transition-colors" title="Imprimir Recibo">
                                                    <Printer className="w-4 h-4" />
                                                </button>

                                                {invoice.pdfUrl && (
                                                    <button onClick={() => sendInvoiceEmail(invoice._id)} className="text-blue-500 hover:bg-blue-50 p-1 rounded transition-colors" title="Enviar Boleto por Email">
                                                        <Mail className="w-4 h-4" />
                                                    </button>
                                                )}

                                                {invoice.nfseId && (
                                                    <>
                                                        <button
                                                            onClick={() => handleSendCombo(invoice)}
                                                            disabled={comboSendingId === invoice._id}
                                                            className="text-emerald-600 hover:bg-emerald-50 p-1 rounded transition-colors"
                                                            title="Enviar Combo Completo (Boleto PDF + DANFSe PDF + XML por E-mail)"
                                                        >
                                                            {comboSendingId === invoice._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                                        </button>
                                                        <button
                                                            onClick={() => handleSendComboWhatsApp(invoice)}
                                                            className="text-green-600 hover:bg-green-50 p-1 rounded transition-colors"
                                                            title="Enviar Boleto e Link da NFS-e por WhatsApp"
                                                        >
                                                            <MessageSquare className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                                {invoice.status !== 'paid' && invoice.status !== 'canceled' && (
                                                    <>
                                                        <button onClick={() => markAsPaid(invoice._id)} className="text-green-500 hover:bg-green-50 p-1 rounded transition-colors" title="Marcar como Pago">
                                                            <CheckCircle className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => deleteInvoice(invoice._id)} className="text-red-500 hover:bg-red-50 p-1 rounded transition-colors" title="Cancelar/Excluir">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'recurrence' && (
                    <div className="max-w-6xl mx-auto space-y-6">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center"><Repeat className="mr-2 text-blue-600" /> Gestão de Recorrências</h2>

                            {/* Search & Actions */}
                            <div className="flex gap-2 w-full md:w-auto">
                                <div className="relative flex-1 md:w-64">
                                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Buscar por cliente..."
                                        className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                        value={recurrenceSearch}
                                        onChange={e => {
                                            setRecurrenceSearch(e.target.value);
                                            setRecurrencePage(1); // Reset page on search
                                        }}
                                    />
                                </div>
                                <button onClick={handlePrintRecurrences} className="bg-white border hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm" title="Imprimir Lista">
                                    <Printer className="w-4 h-4 mr-2" />
                                </button>
                                <button onClick={handleExportRecurrences} className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg font-bold flex items-center transition-colors text-sm shadow-sm" title="Exportar CSV">
                                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-gray-50 text-gray-600 uppercase font-bold border-b">
                                    <tr>
                                        <th className="px-6 py-3">Cliente</th>
                                        <th className="px-6 py-3">Frequência</th>
                                        <th className="px-6 py-3 text-right">Valor</th>
                                        <th className="px-6 py-3 text-center">Vínculo NFS-e</th>
                                        <th className="px-6 py-3 text-center">Próxima Execução</th>
                                        <th className="px-6 py-3 text-center">Status</th>
                                        <th className="px-6 py-3 text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {recurrences.map(rec => (
                                        <tr key={rec._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium">{rec.payerName || rec.payerId?.name}</td>
                                            <td className="px-6 py-4">
                                                {rec.interval === 'monthly' ? 'Mensal' : rec.interval === 'weekly' ? 'Semanal' : `${rec.customIntervalDays} dias`}
                                                {rec.durationMode === 'fixed' && <span className="text-xs text-gray-500 block">({rec.installmentsGenerated}/{rec.totalInstallments})</span>}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <span className="font-bold text-green-600">
                                                        R$ {rec.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </span>
                                                    <button
                                                        onClick={() => handleOpenQuickAmountModal(rec)}
                                                        className="p-1 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                                                        title="Editar valor (sincroniza automaticamente com a NFS-e)"
                                                    >
                                                        <DollarSign className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                {(rec.emitNfse || rec.nfseRecurrenceId) && (
                                                    <span className="text-[10px] text-gray-400 block text-right font-medium">Sincronizado c/ NFS-e</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {rec.emitNfse || rec.nfseRecurrenceId ? (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 rounded-full font-semibold flex items-center gap-1">
                                                            <Check className="w-3 h-3 text-emerald-600" /> NFS-e Vinculada
                                                        </span>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleOpenLinkNfseModal(rec)}
                                                                className="text-[11px] text-blue-600 hover:underline"
                                                            >
                                                                Configurar
                                                            </button>
                                                            <button
                                                                onClick={() => handleUnlinkNfse(rec)}
                                                                className="text-[11px] text-red-500 hover:underline"
                                                            >
                                                                Desvincular
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleOpenLinkNfseModal(rec)}
                                                        className="px-2.5 py-1 text-xs border border-dashed border-gray-300 hover:border-blue-500 text-gray-600 hover:text-blue-600 rounded-md font-medium transition-colors flex items-center gap-1 mx-auto"
                                                    >
                                                        <Link className="w-3 h-3" /> + Vincular NFS-e
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center text-gray-600">{rec.nextRun ? new Date(rec.nextRun).toLocaleDateString() : '-'}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${rec.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                                                    {rec.status === 'active' ? 'Ativo' : 'Pausado'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center flex justify-center gap-2">
                                                {(rec.emitNfse || rec.nfseRecurrenceId) && (
                                                    <button
                                                        onClick={() => handleTriggerCombo(rec)}
                                                        className="text-amber-600 hover:bg-amber-50 p-1 rounded transition-colors"
                                                        title="Disparar Combo AGORA (Boleto + NFS-e)"
                                                    >
                                                        <Zap className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button onClick={() => toggleRecurrenceStatus(rec)} className="text-gray-500 hover:text-blue-600" title={rec.status === 'active' ? 'Pausar' : 'Retomar'}>
                                                    {rec.status === 'active' ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                                                </button>
                                                <button onClick={() => handleEditRecurrence(rec)} className="text-blue-500 hover:bg-blue-600" title="Editar"><Edit className="w-4 h-4" /></button>
                                                <button onClick={() => deleteRecurrence(rec._id)} className="text-red-500 hover:bg-red-600" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
                            <div>
                                Mostrando {recurrences.length} de {recurrenceTotalItems} registros
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setRecurrencePage(Math.max(1, recurrencePage - 1))}
                                    disabled={recurrencePage === 1}
                                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                                >
                                    Anterior
                                </button>
                                <span className="px-2 py-1">Página {recurrencePage} de {recurrenceTotalPages}</span>
                                <button
                                    onClick={() => setRecurrencePage(Math.min(recurrenceTotalPages, recurrencePage + 1))}
                                    disabled={recurrencePage >= recurrenceTotalPages}
                                    className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
                                >
                                    Próxima
                                </button>
                            </div>
                        </div>

                    </div>
                )}

                {activeTab === 'issue' && (
                    <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-sm border">
                        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center"><PlusCircle className="mr-2 text-blue-600" /> Emitir Nova Cobrança</h2>

                        <div className="space-y-4">
                            {/* Cliente */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Cliente (Empresa)</label>
                                <select
                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    value={newItem.payerId}
                                    onChange={handleClientSelect}
                                >
                                    <option value="">Selecione um cliente...</option>
                                    {externalCompanies.map(c => (
                                        <option key={c._id} value={c._id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Tipo de Cobrança */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Tipo de Cobrança</label>
                                <div className="grid grid-cols-3 gap-3">
                                    <button onClick={() => setNewItem({ ...newItem, type: 'one_time' })} className={`py-2 px-3 rounded-lg border text-sm font-bold ${newItem.type === 'one_time' ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>À vista</button>
                                    <button onClick={() => setNewItem({ ...newItem, type: 'installments' })} className={`py-2 px-3 rounded-lg border text-sm font-bold ${newItem.type === 'installments' ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>Parcelado</button>
                                    <button onClick={() => setNewItem({ ...newItem, type: 'recurring' })} className={`py-2 px-3 rounded-lg border text-sm font-bold ${newItem.type === 'recurring' ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>Recorrente</button>
                                </div>
                            </div>

                            {/* Campos Comuns */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Valor {newItem.type === 'installments' ? 'Total' : ''} (R$)</label>
                                    <input type="number" step="0.01" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={newItem.amount} onChange={e => setNewItem({ ...newItem, amount: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">{newItem.type === 'recurring' ? 'Início da Cobrança' : 'Vencimento'}</label>
                                    <input type="date" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={newItem.dueDate} onChange={e => setNewItem({ ...newItem, dueDate: e.target.value })} />
                                </div>
                            </div>

                            {/* Condicionais: Parcelado */}
                            {newItem.type === 'installments' && (
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Número de Parcelas</label>
                                    <input type="number" min="2" max="60" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={newItem.installments} onChange={e => setNewItem({ ...newItem, installments: e.target.value })} />
                                    <p className="text-xs text-gray-500 mt-1">Valor por parcela: R$ {(parseFloat(newItem.amount || 0) / parseInt(newItem.installments || 1)).toFixed(2)}</p>
                                </div>
                            )}

                            {/* Condicionais: Recorrente */}
                            {newItem.type === 'recurring' && (
                                <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Intervalo</label>
                                            <select className="w-full p-2 border rounded-md text-sm" value={newItem.recurrenceInterval} onChange={e => setNewItem({ ...newItem, recurrenceInterval: e.target.value })}>
                                                <option value="monthly">Mensal</option>
                                                <option value="weekly">Semanal</option>
                                                <option value="custom">Personalizado</option>
                                            </select>
                                        </div>
                                        {newItem.recurrenceInterval === 'custom' && (
                                            <div>
                                                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Dias</label>
                                                <input type="number" className="w-full p-2 border rounded-md text-sm" value={newItem.customIntervalDays} onChange={e => setNewItem({ ...newItem, customIntervalDays: e.target.value })} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Duração</label>
                                            <select className="w-full p-2 border rounded-md text-sm" value={newItem.recurrenceDuration} onChange={e => setNewItem({ ...newItem, recurrenceDuration: e.target.value })}>
                                                <option value="indefinite">Indefinidamente</option>
                                                <option value="fixed">Quantidade Fixa</option>
                                            </select>
                                        </div>
                                        {newItem.recurrenceDuration === 'fixed' && (
                                            <div>
                                                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Qtd. Vezes</label>
                                                <input type="number" className="w-full p-2 border rounded-md text-sm" value={newItem.recurrenceTotalTimes} onChange={e => setNewItem({ ...newItem, recurrenceTotalTimes: e.target.value })} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div>
                                <textarea className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none h-24" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} placeholder="Detalhes do serviço..." />
                            </div>

                            {newItem.integrationId && (
                                <div className="flex items-center justify-between bg-gray-50 p-3 rounded border">
                                    <div className="text-xs text-gray-500 flex items-center">
                                        <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                                        Integração vinculada: <strong className="ml-1 uppercase">{newItem.integrationId}</strong>
                                    </div>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input type="checkbox" checked={newItem.sendEmail} onChange={e => setNewItem({ ...newItem, sendEmail: e.target.checked })} className="rounded text-blue-600 focus:ring-blue-500" />
                                        <span className="text-sm font-bold text-gray-700 flex items-center"><Mail className="w-4 h-4 mr-1 text-gray-500" /> Enviar por E-mail</span>
                                    </label>
                                </div>
                            )}

                            {/* Card / Opção de Emitir NFS-e Vinculada */}
                            <div className="border border-blue-100 bg-blue-50/50 rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center space-x-2.5 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={newItem.emitNfse}
                                            onChange={e => setNewItem({ ...newItem, emitNfse: e.target.checked })}
                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                                        />
                                        <span className="font-bold text-gray-800 text-sm flex items-center gap-1.5">
                                            <FileText className="w-4 h-4 text-blue-600" />
                                            Emitir Nota Fiscal (NFS-e) Vinculada
                                        </span>
                                    </label>
                                    {newItem.emitNfse && (
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                            Envio Conjunto Ativo
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-600">
                                    Ao marcar, o sistema emitirá automaticamente a NFS-e correspondente com o mesmo valor deste boleto e fará o envio conjunto (Boleto PDF + DANFSe PDF + XML).
                                </p>

                                {newItem.emitNfse && (
                                    <div className="pt-2 border-t border-blue-100 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 mb-1">Cód. Tributação Nacional / LC 116</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="Ex: 01.07.01"
                                                    value={newItem.nfseConfig.tributacaoNacional}
                                                    onChange={e => setNewItem({
                                                        ...newItem,
                                                        nfseConfig: { ...newItem.nfseConfig, tributacaoNacional: e.target.value }
                                                    })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-700 mb-1">Alíquota ISS (%)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="w-full p-2 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="5.00"
                                                    value={newItem.nfseConfig.aliquota}
                                                    onChange={e => setNewItem({
                                                        ...newItem,
                                                        nfseConfig: { ...newItem.nfseConfig, aliquota: parseFloat(e.target.value) || 0 }
                                                    })}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-700 mb-1">Descrição dos Serviços na NFS-e (opcional)</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder="Deixe em branco para usar a mesma descrição do boleto..."
                                                value={newItem.nfseConfig.description}
                                                onChange={e => setNewItem({
                                                    ...newItem,
                                                    nfseConfig: { ...newItem.nfseConfig, description: e.target.value }
                                                })}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button onClick={handleCreate} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-md mt-4">
                                {newItem.type === 'recurring' ? 'Criar Recorrência' : 'Gerar Boleto(s)'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Editar Recorrência" confirmText="Salvar Alterações" onConfirm={handleSaveRecurrence}>
                {editingRecurrence && (
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto p-2">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Cliente</label>
                            <input type="text" disabled className="w-full p-3 border rounded-lg bg-gray-100 text-gray-500" value={editingRecurrence.payerName} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Valor (R$)</label>
                                <input type="number" step="0.01" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={editingRecurrence.amount} onChange={e => setEditingRecurrence({ ...editingRecurrence, amount: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Próxima Execução</label>
                                <input type="date" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" value={editingRecurrence.dueDate} onChange={e => setEditingRecurrence({ ...editingRecurrence, dueDate: e.target.value })} />
                            </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Intervalo</label>
                                    <select className="w-full p-2 border rounded-md text-sm" value={editingRecurrence.recurrenceInterval} onChange={e => setEditingRecurrence({ ...editingRecurrence, recurrenceInterval: e.target.value })}>
                                        <option value="monthly">Mensal</option>
                                        <option value="weekly">Semanal</option>
                                        <option value="custom">Personalizado</option>
                                    </select>
                                </div>
                                {editingRecurrence.recurrenceInterval === 'custom' && (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Dias</label>
                                        <input type="number" className="w-full p-2 border rounded-md text-sm" value={editingRecurrence.customIntervalDays} onChange={e => setEditingRecurrence({ ...editingRecurrence, customIntervalDays: e.target.value })} />
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Duração</label>
                                    <select className="w-full p-2 border rounded-md text-sm" value={editingRecurrence.recurrenceDuration} onChange={e => setEditingRecurrence({ ...editingRecurrence, recurrenceDuration: e.target.value })}>
                                        <option value="indefinite">Indefinidamente</option>
                                        <option value="fixed">Quantidade Fixa</option>
                                    </select>
                                </div>
                                {editingRecurrence.recurrenceDuration === 'fixed' && (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Qtd. Vezes</label>
                                        <input type="number" className="w-full p-2 border rounded-md text-sm" value={editingRecurrence.recurrenceTotalTimes} onChange={e => setEditingRecurrence({ ...editingRecurrence, recurrenceTotalTimes: e.target.value })} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Descrição</label>
                            <textarea className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none h-24" value={editingRecurrence.description} onChange={e => setEditingRecurrence({ ...editingRecurrence, description: e.target.value })} placeholder="Detalhes do serviço..." />
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal de Alteração Rápida de Valor com Sincronização NFS-e */}
            {quickAmountModalRecurrence && (
                <Modal
                    isOpen={!!quickAmountModalRecurrence}
                    onClose={() => setQuickAmountModalRecurrence(null)}
                    title="Editar Valor do Boleto Recorrente"
                    confirmText="Salvar e Sincronizar"
                    onConfirm={handleSaveQuickAmount}
                >
                    <div className="space-y-4 p-2">
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                            <strong>Sincronização Automática:</strong> Ao atualizar o valor deste boleto, a Nota Fiscal de Serviço (NFS-e) vinculada será automaticamente atualizada para o mesmo valor, garantindo total conformidade fiscal.
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Cliente</label>
                            <input
                                type="text"
                                disabled
                                className="w-full p-2.5 border rounded-lg bg-gray-100 text-gray-600 font-medium"
                                value={quickAmountModalRecurrence.payerName || quickAmountModalRecurrence.payerId?.name}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Novo Valor da Cobrança (R$)</label>
                            <input
                                type="number"
                                step="0.01"
                                className="w-full p-2.5 border rounded-lg text-lg font-bold text-green-700 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={quickAmountValue}
                                onChange={e => setQuickAmountValue(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de Vínculo de NFS-e */}
            {linkNfseModalRecurrence && (
                <Modal
                    isOpen={!!linkNfseModalRecurrence}
                    onClose={() => setLinkNfseModalRecurrence(null)}
                    title={`Configurar NFS-e: ${linkNfseModalRecurrence.payerName || linkNfseModalRecurrence.payerId?.name}`}
                    confirmText="Vincular e Ativar NFS-e"
                    onConfirm={handleSaveLinkNfse}
                >
                    <div className="space-y-4 p-2">
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                            A Nota Fiscal será gerada automaticamente todo mês na mesma data do boleto, com o mesmo valor de <strong>R$ {linkNfseModalRecurrence.amount?.toFixed(2)}</strong>. Ambos serão disparados em conjunto (Boleto PDF + DANFSe PDF + XML).
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Cód. Trib. Nacional / LC 116</label>
                                <input
                                    type="text"
                                    className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={linkNfseForm.tributacaoNacional}
                                    onChange={e => setLinkNfseForm({ ...linkNfseForm, tributacaoNacional: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Alíquota ISS (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={linkNfseForm.aliquota}
                                    onChange={e => setLinkNfseForm({ ...linkNfseForm, aliquota: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Descrição do Serviço</label>
                            <textarea
                                className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none h-20"
                                value={linkNfseForm.description}
                                onChange={e => setLinkNfseForm({ ...linkNfseForm, description: e.target.value })}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div >
    );
};

export default FinanceiroScreen;
